import { useState, useEffect, useCallback, useRef } from 'react';
import { useLang } from '../context/LangContext';
import '../css/welcome-tips.css';

/* ── Language options ─────────────────────────────────── */
const LANGS = [
  { code: 'en', native: 'English',  sub: 'English',  flag: '🇬🇧' },
  { code: 'ar', native: 'العربية', sub: 'Arabic',   flag: '🇸🇦' },
  { code: 'ku', native: 'کوردی',   sub: 'Kurdish',  flag: '🇮🇶' },
];

/* ── Guide steps per language ─────────────────────────── */
const GUIDE = {
  en: [
    { selector: '.btn-primary',         title: '📅 Book a Consultation', desc: 'Tap here to schedule your consultation — pick a service, date & time. We confirm within 24 hours.' },
    { selector: '.btn-pdf',             title: '📄 Company Profile',     desc: 'View the AMKO Agency company profile — all services, portfolio highlights and what we offer.' },
    { selector: '.lang-toggle-desktop', title: '🌐 Switch Language',     desc: 'Switch between English, Arabic and Kurdish instantly.',
      selectorFallback: '.navbar-toggle', descFallback: 'Open the ☰ menu and scroll down — you\'ll find the language options there.' },
    { selector: '.whatsapp-btn',        title: '💬 WhatsApp',            desc: 'Message us directly anytime from this button. Fast replies guaranteed!' },
    { selector: '#reviews',             title: '⭐ Leave a Review',       desc: 'Scroll down to the Reviews section to share your experience with us.', scroll: true },
  ],
  ar: [
    { selector: '.btn-primary',         title: '📅 احجز استشارة',      desc: 'اضغط هنا لحجز استشارتك — اختار الخدمة والتاريخ والوقت. نأكد خلال ٢٤ ساعة.' },
    { selector: '.btn-pdf',             title: '📄 بروفايل الشركة',    desc: 'شوف بروفايل وكالة AMKO — الخدمات، الأعمال، وكل ما نقدمه.' },
    { selector: '.lang-toggle-desktop', title: '🌐 تغيير اللغة',       desc: 'غيّر اللغة بين العربية والإنجليزية والكردية بضغطة واحدة.',
      selectorFallback: '.navbar-toggle', descFallback: 'افتح القائمة ☰ ومرر للأسفل — تلقى خيارات اللغة هناك.' },
    { selector: '.whatsapp-btn',        title: '💬 واتساب',             desc: 'راسلنا مباشرة في أي وقت. رد سريع مضمون!' },
    { selector: '#reviews',             title: '⭐ اترك تقييماً',       desc: 'مرر للأسفل لقسم التقييمات وشارك تجربتك معنا.', scroll: true },
  ],
  ku: [
    { selector: '.btn-primary',         title: '📅 شوێنپێچوون',        desc: 'ئێرە دابەزێنە بۆ تۆمارکردنی شوێنپێچوون — خزمەتگوزاری، بەروار و کات هەڵبژێرە. لە ٢٤ کاتژمێردا پشتدەڵنیا دەبین.' },
    { selector: '.btn-pdf',             title: '📄 پرۆفایلی کۆمپانیا', desc: 'پرۆفایلی ئەژانسی AMKO ببینە — خزمەتگوزاریەکان، کارەکان و هەموو ئەوەی پێشکەش دەکەین.' },
    { selector: '.lang-toggle-desktop', title: '🌐 گۆڕینی زمان',       desc: 'زمان بگۆڕە لە کوردی، عەرەبی و ئینگلیزی بە یەک کرتە.',
      selectorFallback: '.navbar-toggle', descFallback: 'مێنووی ☰ بکەوە و خوارەوە بڵێزە — بژاردەکانی زمان ئەوێ دەدۆزیتەوە.' },
    { selector: '.whatsapp-btn',        title: '💬 واتساپ',             desc: 'بە ڕاستەوخۆ لەگەڵمان پەیوەندی بکە. وەڵامی خێرا دەدەینەوە!' },
    { selector: '#reviews',             title: '⭐ هەڵسەنگاندن بنووسە', desc: 'خوارەوە بڵێزە بۆ بەشی هەڵسەنگاندنەکان و ئەزموونەکەت هاوبەش بکە.', scroll: true },
  ],
};

const PAD = 10;

export default function WelcomeTips({ introReady = true }) {
  const { setLang } = useLang();
  const [phase, setPhase] = useState('lang');   // 'lang' | 'guide'
  const [visible, setVisible] = useState(false);
  const [guideLang, setGuideLang] = useState('en');
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);
  const rafRef   = useRef(null);
  const timerRef = useRef(null);

  const cancelPending = useCallback(() => {
    if (rafRef.current)   { cancelAnimationFrame(rafRef.current); rafRef.current   = null; }
    if (timerRef.current) { clearTimeout(timerRef.current);        timerRef.current = null; }
  }, []);

  useEffect(() => () => cancelPending(), [cancelPending]);

  /* ── Measure: jump to element instantly, then measure on next frame ── */
  const measure = useCallback((idx, lang) => {
    cancelPending();
    const steps = GUIDE[lang] || GUIDE.en;
    const s = steps[idx];
    if (!s) return;

    let el = document.querySelector(s.selector);
    if (!el || getComputedStyle(el).display === 'none') {
      el = s.selectorFallback ? document.querySelector(s.selectorFallback) : null;
    }
    if (!el) return;

    const isMobile = window.innerWidth < 1024;
    const sheetH   = isMobile ? 280 : 0;

    // Target: element centred above bottom sheet
    const targetY = Math.max(0,
      el.getBoundingClientRect().top + window.scrollY
      - (window.innerHeight - sheetH) / 2
    );

    // Jump instantly (no animation) — the spotlight CSS transition
    // handles the smooth visual glide to the new position
    if (Math.abs(targetY - window.scrollY) > 40) {
      window.scrollTo({ top: targetY, behavior: 'instant' });
    }

    // Measure on the very next paint frame — no timeout needed
    rafRef.current = requestAnimationFrame(() => {
      setRect(el.getBoundingClientRect());
    });
  }, [cancelPending]);

  /* ── Show on first visit ── */
  useEffect(() => {
    if (!introReady) return;
    if (!localStorage.getItem('amko_visited')) {
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, [introReady]);

  /* ── Re-measure on resize ── */
  useEffect(() => {
    if (!visible || phase !== 'guide') return;
    let t;
    const onResize = () => { clearTimeout(t); t = setTimeout(() => measure(step, guideLang), 120); };
    window.addEventListener('resize', onResize, { passive: true });
    return () => { window.removeEventListener('resize', onResize); clearTimeout(t); };
  }, [step, visible, phase, measure, guideLang]);

  /* ── While guide is active: mark body so FadeInSection elements reveal,
        but DON'T lock overflow — we need programmatic scroll to work.
        The wt-dim overlay already blocks manual user scrolling. ── */
  useEffect(() => {
    if (visible && phase === 'guide') {
      document.body.classList.add('guide-active');
    } else {
      document.body.classList.remove('guide-active');
    }
    return () => document.body.classList.remove('guide-active');
  }, [visible, phase]);

  const pickLang = (code) => {
    setLang(code);
    setGuideLang(code);
    setPhase('guide');
    setStep(0);
    setRect(null);
    // Scroll back to top first, then measure step 0
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => measure(0, code), 400);
  };

  const finish = () => {
    localStorage.setItem('amko_visited', 'true');
    setVisible(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const skip = () => {
    setVisible(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goTo = (idx) => {
    setStep(idx);
    measure(idx, guideLang);
  };

  if (!visible) return null;

  /* ── Phase 1: Language picker ── */
  if (phase === 'lang') return (
    <div className="wt-lang-overlay">
      <div className="wt-lang-card">
        <img src="/logo.png" alt="AMKO" className="wt-lang-logo" />
        <h2 className="wt-lang-heading">Welcome to AMKO</h2>
        <p className="wt-lang-sub">Choose your language to start the guide</p>
        <div className="wt-lang-options">
          {LANGS.map(l => (
            <button key={l.code} className="wt-lang-btn" onClick={() => pickLang(l.code)}>
              <span className="wt-lang-flag">{l.flag}</span>
              <span className="wt-lang-native">{l.native}</span>
              <span className="wt-lang-label">{l.sub}</span>
            </button>
          ))}
        </div>
        <button className="wt-skip" style={{ marginTop: '1.25rem' }} onClick={skip}>
          Skip tour
        </button>
      </div>
    </div>
  );

  /* ── Phase 2: Guide ── */
  if (!rect) return null;

  const steps = GUIDE[guideLang] || GUIDE.en;
  const s = steps[step];

  // Use fallback desc when the fallback element was used
  const primaryEl = document.querySelector(s.selector);
  const usingFallback = !primaryEl || getComputedStyle(primaryEl).display === 'none';
  const desc = usingFallback && s.descFallback ? s.descFallback : s.desc;

  const isTouch     = window.matchMedia('(hover: none) and (pointer: coarse)').matches;
  const isMobile    = isTouch || window.innerWidth < 1024;
  const targetEl    = usingFallback
    ? (s.selectorFallback ? document.querySelector(s.selectorFallback) : null)
    : primaryEl;
  const isFixed     = targetEl && getComputedStyle(targetEl).position === 'fixed';
  const isSheet     = isMobile && !isFixed;

  /* Spotlight */
  const spotlight = {
    position: 'fixed',
    top:    rect.top    - PAD,
    left:   rect.left   - PAD,
    width:  rect.width  + PAD * 2,
    height: rect.height + PAD * 2,
    borderRadius: 12,
    boxShadow: '0 0 0 9999px rgba(10,15,30,0.78), 0 0 0 2px rgba(245,166,35,0.9)',
    zIndex: 1000,
    pointerEvents: 'none',
    transition: 'top 0.38s cubic-bezier(0.4,0,0.2,1), left 0.38s cubic-bezier(0.4,0,0.2,1), width 0.3s cubic-bezier(0.4,0,0.2,1), height 0.3s cubic-bezier(0.4,0,0.2,1)',
    animation: 'wt-spotlight-pulse 2s ease-in-out infinite',
  };

  /* Tooltip position */
  const TIP_W  = Math.min(320, window.innerWidth - 32);
  const TIP_H  = 190;
  const MARGIN = 14;
  const tip = { position: 'fixed', zIndex: 1001 };
  let arrowClass  = '';
  let arrowOffset = '50%';

  if (isSheet) {
    tip.bottom = 0; tip.left = 0; tip.right = 0; tip.width = 'auto';
    arrowClass = 'sheet';
  } else if (isFixed) {
    const w = Math.min(260, window.innerWidth - 32);
    tip.width = w;
    const onRight = rect.left > window.innerWidth / 2;
    tip.left = onRight ? Math.max(MARGIN, rect.right - w) : Math.min(rect.left, window.innerWidth - w - MARGIN);
    const ax = (rect.left + rect.width / 2) - tip.left;
    arrowOffset = `${Math.max(10, Math.min(90, (ax / w) * 100))}%`;
    tip.top = Math.max(MARGIN, rect.top - TIP_H - 24);
    arrowClass = 'arrow-bottom';
  } else {
    tip.width = TIP_W;
    const cx   = rect.left + rect.width / 2;
    const raw  = cx - TIP_W / 2;
    const left = Math.max(MARGIN, Math.min(raw, window.innerWidth - TIP_W - MARGIN));
    tip.left = left;
    arrowOffset = `${Math.max(10, Math.min(90, ((cx - left) / TIP_W) * 100))}%`;
    const spaceBelow = window.innerHeight - rect.bottom - PAD - MARGIN;
    const spaceAbove = rect.top - PAD - MARGIN;
    if (spaceBelow >= TIP_H || spaceBelow >= spaceAbove) {
      tip.top = rect.bottom + PAD + MARGIN; arrowClass = 'arrow-top';
    } else {
      tip.top = Math.max(MARGIN, rect.top - TIP_H - PAD - MARGIN); arrowClass = 'arrow-bottom';
    }
  }

  return (
    <>
      <div className="wt-dim" onClick={() => !isSheet && skip()} onWheel={e => e.preventDefault()} onTouchMove={e => e.preventDefault()} />
      <div style={spotlight} />
      <div className={`wt-tip ${arrowClass}${isFixed ? ' wt-tip-no-sheet' : ''}`} style={{ ...tip, '--arrow-offset': arrowOffset }}>
        <div className="wt-progress">
          {steps.map((_, i) => <div key={i} className={`wt-bar ${i <= step ? 'filled' : ''}`} />)}
        </div>
        <h3 className="wt-title">{s.title}</h3>
        <p className="wt-desc">{desc}</p>
        <div className="wt-footer">
          <span className="wt-count">{step + 1} / {steps.length}</span>
          <div className="wt-actions">
            {step > 0 && (
              <button className="wt-btn-ghost" onClick={() => goTo(step - 1)}>
                {guideLang === 'en' ? '← Back' : guideLang === 'ar' ? 'رجوع →' : 'گەڕانەوە →'}
              </button>
            )}
            <button className="wt-btn-primary" onClick={() => step < steps.length - 1 ? goTo(step + 1) : finish()}>
              {step < steps.length - 1
                ? (guideLang === 'en' ? 'Next →' : '← التالي')
                : (guideLang === 'en' ? '✓ Done' : guideLang === 'ar' ? '✓ تم' : '✓ تەواو')}
            </button>
          </div>
        </div>
        <button className="wt-skip" onClick={skip}>Skip tour</button>
      </div>
    </>
  );
}
